import { useMemo, useState } from 'react';
import { api, ApiError, type HeatGenerationOptions } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Alert, EmptyState, LoadingBlock, Spinner } from '../components/ui';
import { ConfirmDialog, Modal } from '../components/Modal';
import { CheckboxField, SelectField, TextField } from '../components/Field';
import { formatClock, formatDateTime, formatSwimTimeShort, isoToLocalInput, localInputToIso } from '../lib/format';
import {
  ENTRY_STATUS_OPTIONS,
  type AdminEvent,
  type AdminHeat,
  type EntryStatus,
  type HeatSetMeta,
  type HeatsResponse,
} from '../lib/types';

interface GenerationForm {
  includeStatuses: EntryStatus[];
  laneCount: string;
  seedingMode: 'lanes' | 'waves';
  balanceHeats: boolean;
  groupByCategory: boolean;
  heatDurationMinutes: string;
  changeoverMinutes: string;
  firstHeatAt: string;
}

export function HeatsPage() {
  const event = useAsync<AdminEvent>((signal) => api.admin.getEvent(signal), []);
  const heats = useAsync<HeatsResponse>((signal) => api.admin.getHeats(signal), []);

  const [form, setForm] = useState<GenerationForm | null>(null);
  const [preview, setPreview] = useState<{ heats: AdminHeat[]; meta: HeatSetMeta | null } | null>(
    null,
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'publish' | 'unpublish' | 'clear' | 'apply' | null>(
    null,
  );
  const [moving, setMoving] = useState<{ entryId: string; name: string; heatNumber: number } | null>(
    null,
  );

  // Seed the generation form from the event once it loads.
  const effectiveForm: GenerationForm | null = useMemo(() => {
    if (form) return form;
    if (!event.data) return null;
    const e = event.data;
    const firstHeat = new Date(`${e.eventDate}T${e.startTime}:00Z`);
    return {
      includeStatuses: e.heatsGeneratedAt
        ? (heats.data?.meta?.params.includeStatuses ?? ['pending', 'confirmed'])
        : ['pending', 'confirmed'],
      laneCount: String(e.laneCount),
      seedingMode: e.seedingMode,
      balanceHeats: heats.data?.meta?.params.balanceHeats ?? true,
      groupByCategory: heats.data?.meta?.params.groupByCategory ?? true,
      heatDurationMinutes: String(
        heats.data?.meta?.params.heatDurationMinutes ?? e.heatDurationMinutes,
      ),
      changeoverMinutes: String(heats.data?.meta?.params.changeoverMinutes ?? e.changeoverMinutes),
      firstHeatAt: Number.isNaN(firstHeat.getTime())
        ? ''
        : isoToLocalInput(firstHeat.toISOString()),
    };
  }, [form, event.data, heats.data]);

  function set<K extends keyof GenerationForm>(key: K, value: GenerationForm[K]) {
    if (!effectiveForm) return;
    setForm({ ...effectiveForm, [key]: value });
  }

  function buildOptions(dryRun: boolean): HeatGenerationOptions | null {
    if (!effectiveForm) return null;
    const laneCount = Number(effectiveForm.laneCount);
    const heatDurationMinutes = Number(effectiveForm.heatDurationMinutes);
    const changeoverMinutes = Number(effectiveForm.changeoverMinutes);
    if (!Number.isFinite(laneCount) || laneCount < 1) {
      setError('Enter a valid number of lanes or swimmers per wave.');
      return null;
    }
    if (!Number.isFinite(heatDurationMinutes) || heatDurationMinutes < 1) {
      setError('Enter a valid default heat duration in minutes.');
      return null;
    }
    if (effectiveForm.changeoverMinutes.trim() === '') {
      setError('Enter a changeover time in minutes. Use 0 for back-to-back heats.');
      return null;
    }
    if (!Number.isFinite(changeoverMinutes) || changeoverMinutes < 0) {
      setError('Enter a valid changeover time in minutes.');
      return null;
    }
    if (effectiveForm.includeStatuses.length === 0) {
      setError('Select at least one entry status to include.');
      return null;
    }
    return {
      includeStatuses: effectiveForm.includeStatuses,
      laneCount,
      seedingMode: effectiveForm.seedingMode,
      balanceHeats: effectiveForm.balanceHeats,
      groupByCategory: effectiveForm.groupByCategory,
      heatDurationMinutes,
      changeoverMinutes,
      firstHeatAt: localInputToIso(effectiveForm.firstHeatAt) || undefined,
      dryRun,
    };
  }

  async function run(action: string, work: () => Promise<void>) {
    setBusy(action);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  const onPreview = () =>
    run('preview', async () => {
      const options = buildOptions(true);
      if (!options) return;
      const result = await api.admin.generateHeats(options);
      setPreview({ heats: result.heats, meta: result.meta });
      setNotice(null);
    });

  const onApply = () =>
    run('apply', async () => {
      const options = buildOptions(false);
      if (!options) return;
      const result = await api.admin.generateHeats(options);
      setPreview(null);
      setConfirming(null);
      setNotice(
        `Generated ${result.meta?.heatCount ?? 0} ${
          options.seedingMode === 'waves' ? 'waves' : 'heats'
        } for ${result.meta?.swimmerCount ?? 0} swimmers. Start lists are unpublished until you publish them.`,
      );
      heats.reload();
      event.reload();
    });

  const onPublish = () =>
    run('publish', async () => {
      await api.admin.publishHeats();
      setConfirming(null);
      setNotice('Start lists are now visible to competitors.');
      heats.reload();
      event.reload();
    });

  const onUnpublish = () =>
    run('unpublish', async () => {
      await api.admin.unpublishHeats();
      setConfirming(null);
      setNotice('Start lists are hidden from competitors again.');
      heats.reload();
      event.reload();
    });

  const onClear = () =>
    run('clear', async () => {
      await api.admin.clearHeats();
      setConfirming(null);
      setPreview(null);
      setNotice('Heat set deleted.');
      heats.reload();
      event.reload();
    });

  if (event.loading || heats.loading) return <LoadingBlock message="Loading heats..." />;
  if (event.error || !event.data || !effectiveForm) {
    return (
      <Alert variant="error" title="Could not load the event">
        <p>{event.error ?? 'Please try again.'}</p>
      </Alert>
    );
  }

  const e = event.data;
  const current = heats.data;
  const unit = effectiveForm.seedingMode === 'waves' ? 'wave' : 'heat';
  const unitPlural = `${unit}s`;
  const displayHeats = preview?.heats ?? current?.heats ?? [];
  const isPreviewing = preview !== null;
  // The stored set keeps the settings it was generated with, which is what the
  // schedule on screen reflects; the event's values only apply to the next one.
  const setChangeoverMinutes = current?.meta?.params.changeoverMinutes ?? e.changeoverMinutes;
  const setLaneCount = current?.meta?.params.laneCount ?? e.laneCount;

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1>{unitPlural[0]!.toUpperCase() + unitPlural.slice(1)}</h1>
          <p>
            Seeded from each competitor&apos;s predicted time. Slowest {unitPlural} start first.
          </p>
        </div>
        <div className="row">
          {current && current.heats.length > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() =>
                void run('export', async () => {
                  await api.admin.exportHeats();
                })
              }
            >
              Export CSV
            </button>
          )}
          {current?.published ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setConfirming('unpublish')}
            >
              Unpublish
            </button>
          ) : (
            current &&
            current.heats.length > 0 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setConfirming('publish')}
              >
                Publish start lists
              </button>
            )
          )}
        </div>
      </div>

      {notice && (
        <Alert variant="success">
          <p style={{ marginBottom: 0 }}>{notice}</p>
        </Alert>
      )}
      {error && (
        <Alert variant="error">
          <p style={{ marginBottom: 0 }}>{error}</p>
        </Alert>
      )}

      {e.heatsLocked && (
        <Alert variant="warning" title="Heats are locked">
          <p style={{ marginBottom: 0 }}>
            Regenerating and manual moves are blocked. Unlock in <strong>Settings</strong> to make
            changes.
          </p>
        </Alert>
      )}

      {current?.published ? (
        <Alert variant="success" title="Published">
          <p style={{ marginBottom: 0 }}>
            Competitors can see these start lists. Published {formatDateTime(current.publishedAt)}.
          </p>
        </Alert>
      ) : current && current.heats.length > 0 ? (
        <Alert variant="warning" title="Not published">
          <p style={{ marginBottom: 0 }}>
            These {unitPlural} exist but competitors cannot see them yet.
          </p>
        </Alert>
      ) : null}

      <section className="card stack">
        <h2>Generate {unitPlural}</h2>
        <p className="muted" style={{ marginBottom: 0 }}>
          Preview first to check the split, then apply. Applying replaces any existing {unitPlural}{' '}
          and unpublishes the start lists until you publish again.
        </p>

        <fieldset>
          <legend>Which entries to include</legend>
          <div className="row">
            {ENTRY_STATUS_OPTIONS.map((option) => (
              <CheckboxField
                key={option.value}
                inline
                label={option.label}
                checked={effectiveForm.includeStatuses.includes(option.value)}
                onChange={(checked) =>
                  set(
                    'includeStatuses',
                    checked
                      ? [...effectiveForm.includeStatuses, option.value]
                      : effectiveForm.includeStatuses.filter((s) => s !== option.value),
                  )
                }
              />
            ))}
          </div>
        </fieldset>

        <div className="form-grid">
          <SelectField
            label="Seeding mode"
            hint="Waves for a mass open water start, lanes for a pool."
            options={[
              { value: 'waves', label: 'Waves (open water)' },
              { value: 'lanes', label: 'Lanes (pool)' },
            ]}
            value={effectiveForm.seedingMode}
            onChange={(ev) => set('seedingMode', ev.target.value as 'lanes' | 'waves')}
          />
          <TextField
            label={effectiveForm.seedingMode === 'waves' ? 'Swimmers per wave' : 'Lanes per heat'}
            type="number"
            min={1}
            max={60}
            value={effectiveForm.laneCount}
            onChange={(ev) => set('laneCount', ev.target.value)}
          />
          <TextField
            label={`Default ${unit} duration (minutes)`}
            hint={`Used when a ${unit} has no usable predicted times. Otherwise each ${unit} is as long as its slowest swimmer, rounded up to 5 minutes.`}
            type="number"
            min={1}
            max={240}
            value={effectiveForm.heatDurationMinutes}
            onChange={(ev) => set('heatDurationMinutes', ev.target.value)}
          />
          <TextField
            label={`Minutes between ${unitPlural} (changeover)`}
            hint={`Clearing time added between every pair of ${unitPlural}.`}
            type="number"
            min={0}
            max={120}
            value={effectiveForm.changeoverMinutes}
            onChange={(ev) => set('changeoverMinutes', ev.target.value)}
          />
          <TextField
            label={`First ${unit} start (UTC)`}
            type="datetime-local"
            value={effectiveForm.firstHeatAt}
            onChange={(ev) => set('firstHeatAt', ev.target.value)}
          />
        </div>

        <CheckboxField
          label={`Even out ${unitPlural} so the last one is not left with only a couple of swimmers`}
          checked={effectiveForm.balanceHeats}
          onChange={(checked) => set('balanceHeats', checked)}
        />
        <CheckboxField
          label="Seed each category separately (categories flagged in settings)"
          checked={effectiveForm.groupByCategory}
          onChange={(checked) => set('groupByCategory', checked)}
        />

        <div className="row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void onPreview()}
            disabled={busy !== null}
          >
            {busy === 'preview' ? (
              <>
                <Spinner label="Building preview" /> Building preview...
              </>
            ) : (
              'Preview'
            )}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setConfirming('apply')}
            disabled={busy !== null || e.heatsLocked}
          >
            Generate and save
          </button>
          {current && current.heats.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setConfirming('clear')}
              disabled={busy !== null || e.heatsLocked}
            >
              Delete all {unitPlural}
            </button>
          )}
        </div>
      </section>

      {isPreviewing && (
        <Alert variant="info" title="Preview only - nothing has been saved">
          <p style={{ marginBottom: 0 }}>
            {preview.meta?.heatCount ?? 0} {unitPlural}, {preview.meta?.swimmerCount ?? 0} swimmers.{' '}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}>
              Discard preview
            </button>
          </p>
        </Alert>
      )}

      {current?.meta && !isPreviewing && (
        <p className="faint">
          Generated {formatDateTime(current.meta.generatedAt)}
          {current.meta.generatedBy && <> by {current.meta.generatedBy}</>} &middot;{' '}
          {current.meta.heatCount} {unitPlural} &middot; {current.meta.swimmerCount} swimmers &middot;
          included: {current.meta.params.includeStatuses.join(', ')}
        </p>
      )}

      {displayHeats.length > 0 && !isPreviewing && !e.heatsLocked && (
        <p className="faint">
          Each {unit} is as long as its slowest predicted time, rounded up to 5 minutes, with{' '}
          {setChangeoverMinutes} minutes changeover between them. A duration you set here sticks
          until that {unit}&apos;s swimmers change or the {unitPlural} are regenerated, and every{' '}
          {unit} after it moves to match.
          {current?.published && (
            <>
              {' '}
              <strong>
                These start lists are published, so changing a duration changes the times
                competitors already have.
              </strong>{' '}
              Publish again once you are happy.
            </>
          )}
        </p>
      )}

      {displayHeats.length === 0 ? (
        <EmptyState title={`No ${unitPlural} yet`}>
          <p>
            Choose your options above and preview the split. Entries need a predicted time, which is
            required at sign-up.
          </p>
        </EmptyState>
      ) : (
        displayHeats.map((heat) => (
          <section className="heat" key={heat.heatNumber} aria-labelledby={`h-${heat.heatNumber}`}>
            <div className="heat-header">
              <h3 className="heat-title" id={`h-${heat.heatNumber}`}>
                {heat.name}
              </h3>
              <span className="heat-meta">
                {heat.lanes.length} swimmers
                {heat.scheduledAt && <> &middot; start {formatClock(heat.scheduledAt)}</>}
                {isPreviewing || e.heatsLocked ? (
                  <> &middot; {heat.durationMinutes} min</>
                ) : null}
              </span>
              {!isPreviewing && !e.heatsLocked && (
                <HeatDurationControl
                  heat={heat}
                  onSaved={(minutes, updated) => {
                    const shifted = updated.find((h) => h.heatNumber === heat.heatNumber);
                    setError(null);
                    setNotice(
                      `${heat.name} is now ${minutes} minutes long${
                        shifted?.scheduledAt ? `, starting ${formatClock(shifted.scheduledAt)}` : ''
                      }. Later ${unitPlural} have moved to match.`,
                    );
                    heats.reload();
                  }}
                  onFailed={(message) => {
                    setNotice(null);
                    setError(message);
                  }}
                />
              )}
            </div>
            <table>
              <caption className="visually-hidden">{heat.name}</caption>
              <thead>
                <tr>
                  <th scope="col">{effectiveForm.seedingMode === 'lanes' ? 'Lane' : 'Pos'}</th>
                  <th scope="col">Reference</th>
                  <th scope="col">Name</th>
                  <th scope="col">Club</th>
                  <th scope="col">Age band</th>
                  <th scope="col">Predicted</th>
                  {!isPreviewing && (
                    <th scope="col" className="col-actions">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {heat.lanes.map((lane) => (
                  <tr key={lane.entryId}>
                    <td>
                      <span className="lane-number">{lane.lane}</span>
                    </td>
                    <td className="mono nowrap">{lane.reference}</td>
                    <td>
                      {lane.firstName} {lane.lastName}
                    </td>
                    <td className="muted">{lane.club || '-'}</td>
                    <td className="muted nowrap">{lane.ageGroup}</td>
                    <td className="mono nowrap">{formatSwimTimeShort(lane.seedTimeMs)}</td>
                    {!isPreviewing && (
                      <td className="col-actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={e.heatsLocked}
                          onClick={() =>
                            setMoving({
                              entryId: lane.entryId,
                              name: `${lane.firstName} ${lane.lastName}`,
                              heatNumber: heat.heatNumber,
                            })
                          }
                        >
                          Move
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))
      )}

      {moving && current && (
        <MoveSwimmerModal
          swimmer={moving}
          heats={current.heats}
          unit={unit}
          laneCount={setLaneCount}
          published={Boolean(current.published)}
          onClose={() => setMoving(null)}
          onMoved={(swapped) => {
            setMoving(null);
            setNotice(
              `${swapped ? 'Swimmers swapped' : 'Swimmer moved'}. Start times have been recalculated.`,
            );
            heats.reload();
          }}
        />
      )}

      {confirming === 'apply' && (
        <ConfirmDialog
          title={`Generate ${unitPlural}?`}
          confirmLabel={`Generate ${unitPlural}`}
          busy={busy === 'apply'}
          message={
            <p style={{ marginBottom: 0 }}>
              This replaces any existing {unitPlural}, including manual moves, and unpublishes the
              start lists until you publish again.
            </p>
          }
          onCancel={() => setConfirming(null)}
          onConfirm={() => void onApply()}
        />
      )}

      {confirming === 'publish' && (
        <ConfirmDialog
          title="Publish start lists?"
          confirmLabel="Publish"
          busy={busy === 'publish'}
          message={
            <p style={{ marginBottom: 0 }}>
              Competitors will be able to see every {unit}, including names, clubs, age bands and
              predicted times. Contact and medical details are never shown.
            </p>
          }
          onCancel={() => setConfirming(null)}
          onConfirm={() => void onPublish()}
        />
      )}

      {confirming === 'unpublish' && (
        <ConfirmDialog
          title="Hide start lists?"
          confirmLabel="Unpublish"
          busy={busy === 'unpublish'}
          message="Competitors will no longer be able to see the start lists."
          onCancel={() => setConfirming(null)}
          onConfirm={() => void onUnpublish()}
        />
      )}

      {confirming === 'clear' && (
        <ConfirmDialog
          title={`Delete all ${unitPlural}?`}
          confirmLabel="Delete"
          danger
          busy={busy === 'clear'}
          message={`Every ${unit} and any manual moves will be removed. Entries themselves are not affected.`}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void onClear()}
        />
      )}
    </div>
  );
}

/**
 * Inline editor for one heat's duration. Saving reschedules every later heat,
 * so the parent reloads the whole set rather than patching this heat in place.
 */
function HeatDurationControl({
  heat,
  onSaved,
  onFailed,
}: {
  heat: AdminHeat;
  onSaved: (durationMinutes: number, heats: AdminHeat[]) => void;
  onFailed: (message: string) => void;
}) {
  const [value, setValue] = useState(String(heat.durationMinutes));
  const [busy, setBusy] = useState(false);
  const minutes = Number(value);
  const changed = value !== String(heat.durationMinutes);

  async function save() {
    if (!Number.isFinite(minutes) || minutes < 1) {
      onFailed('Enter a valid duration in minutes.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.admin.setHeatDuration(heat.heatNumber, minutes);
      onSaved(minutes, result.heats);
    } catch (err) {
      onFailed(err instanceof ApiError ? err.message : 'Could not change the duration.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="row">
      <label className="faint" htmlFor={`dur-${heat.heatNumber}`}>
        Minutes
      </label>
      <input
        id={`dur-${heat.heatNumber}`}
        type="number"
        min={1}
        max={240}
        value={value}
        disabled={busy}
        style={{ width: '5.5rem' }}
        onChange={(ev) => setValue(ev.target.value)}
      />
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={busy || !changed}
        onClick={() => void save()}
      >
        {busy ? (
          <>
            <Spinner label="Saving duration" /> Saving...
          </>
        ) : (
          'Save'
        )}
      </button>
    </span>
  );
}

function MoveSwimmerModal({
  swimmer,
  heats,
  unit,
  laneCount,
  published,
  onClose,
  onMoved,
}: {
  swimmer: { entryId: string; name: string; heatNumber: number };
  heats: AdminHeat[];
  unit: string;
  laneCount: number;
  published: boolean;
  onClose: () => void;
  onMoved: (swapped: boolean) => void;
}) {
  const [heatNumber, setHeatNumber] = useState(String(swimmer.heatNumber));
  const [lane, setLane] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targetHeat = heats.find((h) => h.heatNumber === Number(heatNumber));
  const occupant = targetHeat?.lanes.find(
    (l) => l.lane === Number(lane) && l.entryId !== swimmer.entryId,
  );

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.admin.moveSwimmer(swimmer.entryId, Number(heatNumber), Number(lane));
      onMoved(result.swapped);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not move the swimmer.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Move ${swimmer.name}`}
      narrow
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? (
              <>
                <Spinner label="Moving" /> Moving...
              </>
            ) : occupant ? (
              'Swap places'
            ) : (
              'Move'
            )}
          </button>
        </>
      }
    >
      <div className="stack">
        {error && (
          <Alert variant="error">
            <p style={{ marginBottom: 0 }}>{error}</p>
          </Alert>
        )}

        {published && (
          <Alert variant="warning" title="These start lists are published">
            <p style={{ marginBottom: 0 }}>
              Moving a swimmer changes how long the two {unit}s take, so the start times
              competitors already have will shift. Publish again once you are happy with the
              schedule.
            </p>
          </Alert>
        )}

        <div className="form-grid">
          <SelectField
            label={`Target ${unit}`}
            options={heats.map((h) => ({ value: String(h.heatNumber), label: h.name }))}
            value={heatNumber}
            onChange={(e) => setHeatNumber(e.target.value)}
          />
          <TextField
            label={unit === 'wave' ? 'Position' : 'Lane'}
            type="number"
            min={1}
            max={laneCount}
            value={lane}
            onChange={(e) => setLane(e.target.value)}
          />
        </div>

        {occupant ? (
          <Alert variant="warning" title="That place is taken">
            <p style={{ marginBottom: 0 }}>
              {occupant.firstName} {occupant.lastName} is there now. Continuing will swap the two
              swimmers.
            </p>
          </Alert>
        ) : (
          <p className="faint" style={{ marginBottom: 0 }}>
            Manual moves are kept until the {unit}s are regenerated.
          </p>
        )}
      </div>
    </Modal>
  );
}
